import PropTypes from "prop-types"
import { useMemo, useState } from "react"
import ReactApexChart from "react-apexcharts"
import { Button, Input, InputGroup, InputGroupText, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap"
import {
  MAX_MOVING_AVERAGE_WINDOW,
  buildAdjustmentRows,
  formatCurrency,
  formatDateLabel,
  getAverage,
  getStandardDeviation,
} from "utils/cash-movement-analysis"

const chartPanelStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  boxShadow: "0 8px 22px rgba(15, 23, 42, 0.06)",
  padding: "1.25rem",
}

const tooltipDescriptions = {
  "Ajuste puntual": "Monto real del ajuste registrado en esa fecha.",
  "Media movil": "Promedio suavizado de ajustes recientes.",
  "Banda superior": "Limite superior esperado segun la variacion historica.",
  "Banda inferior": "Limite inferior esperado segun la variacion historica.",
}

const buildAdjustmentChartData = (movements) => {
  const adjustments = buildAdjustmentRows(movements)

  const movingAverageWindow = Math.min(
    MAX_MOVING_AVERAGE_WINDOW,
    Math.max(1, adjustments.length * 2 || 1)
  )

  const labels = adjustments.map((item) => formatDateLabel(item.date))
  const amounts = adjustments.map((item) => item.amount)
  const movingAverage = []
  const upperBand = []
  const lowerBand = []

  amounts.forEach((amount, index) => {
    const start = Math.max(0, index - movingAverageWindow + 1)
    const windowValues = amounts.slice(start, index + 1)
    const average = getAverage(windowValues)
    const standardDeviation = getStandardDeviation(windowValues, average)

    movingAverage.push(Number(average.toFixed(2)))
    upperBand.push(Number((average + standardDeviation * 2).toFixed(2)))
    lowerBand.push(Number((average - standardDeviation * 2).toFixed(2)))
  })

  return {
    labels,
    movingAverageWindow,
    count: amounts.length,
    total: amounts.reduce((total, amount) => total + amount, 0),
    pointSeries: [
      {
        name: "Ajuste",
        data: amounts,
      },
    ],
    series: [
      { name: "Ajuste puntual", type: "column", data: amounts },
      { name: "Media movil", type: "line", data: movingAverage },
      { name: "Banda superior", type: "line", data: upperBand },
      { name: "Banda inferior", type: "line", data: lowerBand },
    ],
  }
}

const getMoneyAxis = () => ({
  labels: {
    formatter: (value) =>
      new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: "MXN",
        maximumFractionDigits: 0,
      }).format(value),
    style: { colors: "#64748b" },
  },
})

const CashMovementsChart = ({ adjustmentMovements, adjustmentLimit, onAdjustmentLimitChange, isLoading }) => {
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [limitDraft, setLimitDraft] = useState(adjustmentLimit)
  const adjustmentChartData = useMemo(() => buildAdjustmentChartData(adjustmentMovements), [adjustmentMovements])

  const pointOptions = useMemo(
    () => ({
      chart: { toolbar: { show: true }, zoom: { enabled: true } },
      colors: ["#2563eb"],
      dataLabels: { enabled: false },
      markers: {
        size: adjustmentChartData.labels.length > 100 ? 4 : 6,
        strokeWidth: 2,
        hover: { size: adjustmentChartData.labels.length > 100 ? 6 : 8 },
      },
      stroke: { curve: "straight", width: 2 },
      grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
      xaxis: {
        categories: adjustmentChartData.labels,
        tickAmount: adjustmentChartData.labels.length > 100 ? 12 : undefined,
        labels: { style: { colors: "#64748b", fontSize: "12px" } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: getMoneyAxis(),
      tooltip: { y: { formatter: (value) => formatCurrency(value) } },
      legend: { show: false },
    }),
    [adjustmentChartData.labels]
  )

  const adjustmentOptions = useMemo(
    () => ({
      chart: {
        toolbar: { show: true },
        zoom: { enabled: true },
      },
      colors: ["#2563eb", "#14b8a6", "#ef4444", "#f59e0b"],
      dataLabels: { enabled: false },
      stroke: {
        curve: "smooth",
        width: [3, 3, 2, 2],
        dashArray: [0, 0, 6, 6],
      },
      grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
      xaxis: {
        categories: adjustmentChartData.labels,
        tickAmount: adjustmentChartData.labels.length > 100 ? 12 : undefined,
        labels: { style: { colors: "#64748b", fontSize: "12px" } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: getMoneyAxis(),
      annotations: {
        yaxis: [
          {
            y: adjustmentLimit,
            borderColor: "#dc2626",
            strokeDashArray: 5,
            label: { text: `Limite +${formatCurrency(adjustmentLimit)}` },
          },
          {
            y: adjustmentLimit * -1,
            borderColor: "#dc2626",
            strokeDashArray: 5,
            label: { text: `Limite -${formatCurrency(adjustmentLimit)}` },
          },
        ],
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: ({ series, dataPointIndex, w }) => {
          const dateLabel = adjustmentChartData.labels[dataPointIndex] || ""
          const rows = w.globals.seriesNames
            .map((name, index) => {
              const value = series[index]?.[dataPointIndex]
              if (value === undefined || value === null) return ""

              const color = w.globals.colors[index]
              const description = tooltipDescriptions[name] || ""

              return `
                <div style="display:flex; gap:8px; align-items:flex-start; padding:4px 0;">
                  <span style="width:9px; height:9px; border-radius:50%; background:${color}; margin-top:5px;"></span>
                  <div>
                    <div style="font-weight:600;">${name}: ${formatCurrency(value)}</div>
                    <div style="color:#64748b; font-size:11px;">${description}</div>
                  </div>
                </div>
              `
            })
            .join("")

          return `
            <div style="padding:10px 12px; min-width:240px;">
              <div style="font-weight:700; margin-bottom:6px;">${dateLabel}</div>
              ${rows}
            </div>
          `
        },
      },
      legend: {
        position: "top",
        horizontalAlign: "left",
      },
    }),
    [adjustmentChartData.labels, adjustmentLimit]
  )

  const openConfigModal = () => {
    setLimitDraft(adjustmentLimit)
    setShowConfigModal(true)
  }

  const saveConfig = () => {
    onAdjustmentLimitChange(limitDraft)
    setShowConfigModal(false)
  }

  return (
    <div className="d-flex flex-column gap-4">
      {isLoading ? (
        <div className="text-center text-muted py-5">Cargando grafica...</div>
      ) : (
        <>
          <section style={chartPanelStyle}>
            <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
              <div>
                <h5 className="mb-1">Ajustes puntuales</h5>
                <p className="text-muted mb-0">Vista simple por fecha.</p>
              </div>
              <div className="d-flex gap-4 text-end">
                <div>
                  <div className="text-muted small">Registros</div>
                  <strong>{adjustmentChartData.count}</strong>
                </div>
                <div>
                  <div className="text-muted small">Acumulado</div>
                  <strong>{formatCurrency(adjustmentChartData.total)}</strong>
                </div>
              </div>
            </div>

            {adjustmentChartData.labels.length ? (
              <ReactApexChart
                options={pointOptions}
                series={adjustmentChartData.pointSeries}
                type="line"
                height={300}
              />
            ) : (
              <div className="text-center text-muted py-5">Todavia no hay ajustes para graficar.</div>
            )}
          </section>

          <section style={chartPanelStyle}>
            <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
              <div className="d-flex align-items-start gap-2 flex-wrap">
                <Button color="light" size="sm" onClick={openConfigModal} title="Configurar grafica">
                  <i className="fa fa-cog" />
                </Button>
                <div>
                  <h5 className="mb-1">Analisis de ajustes</h5>
                  <p className="text-muted mb-0">Media movil y bandas del comportamiento historico.</p>
                </div>
              </div>
              <div className="text-end">
                <div className="text-muted small">Limite acumulado</div>
                <strong>+/- {formatCurrency(adjustmentLimit)}</strong>
                <div className="text-muted small mt-1">Ventana: {adjustmentChartData.movingAverageWindow}</div>
              </div>
            </div>

            {adjustmentChartData.labels.length ? (
              <ReactApexChart
                options={adjustmentOptions}
                series={adjustmentChartData.series}
                type="line"
                height={360}
              />
            ) : (
              <div className="text-center text-muted py-5">Todavia no hay ajustes para graficar.</div>
            )}
          </section>

          <Modal isOpen={showConfigModal} toggle={() => setShowConfigModal(false)} centered>
            <ModalHeader toggle={() => setShowConfigModal(false)}>Configurar grafica de ajustes</ModalHeader>
            <ModalBody>
              <label className="form-label">Limite de ajuste acumulado</label>
              <InputGroup>
                <InputGroupText>$</InputGroupText>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={limitDraft}
                  onChange={(event) => setLimitDraft(event.target.value)}
                />
              </InputGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setShowConfigModal(false)}>
                Cancelar
              </Button>
              <Button color="primary" onClick={saveConfig}>
                Guardar
              </Button>
            </ModalFooter>
          </Modal>
        </>
      )}
    </div>
  )
}

CashMovementsChart.propTypes = {
  adjustmentMovements: PropTypes.array,
  adjustmentLimit: PropTypes.number,
  onAdjustmentLimitChange: PropTypes.func,
  isLoading: PropTypes.bool,
}

CashMovementsChart.defaultProps = {
  adjustmentMovements: [],
  adjustmentLimit: 500,
  onAdjustmentLimitChange: () => {},
  isLoading: false,
}

export default CashMovementsChart
